import type {RootState} from '@/store';
import {createSelector} from '@reduxjs/toolkit';
import type {FabricObject} from '@postermywall/fabricjs-2';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {getItemPageEndTime} from '@PosterWhiteboard/items/item/item-timing.library';
import type {TimeSpan} from '@PosterWhiteboard/page/timeline-group-gestures.library';
import {getAudioTimelineResizeBounds} from '@PosterWhiteboard/page/timeline-group-gestures.library';
import {getItemBarResizeBounds} from './timeline-bar-model';
import {getPlaybackDuration} from '@Libraries/audio-player/audio-player.library';
import type {TimelineBarRef, TimelineGroupGestureMember, TimelineModifyingState} from '../timeline.types';
import {TimelineItemType} from '../timeline.types';
import type {TimelineBarClickOptions} from '../components/timeline-item/timeline-item.types';
import {TimelineMultiSelectType} from '@Libraries/ga-events';
import {trackTimelineMultiSelectOnceIfNeeded} from '@Libraries/poster-editor-tracking.library';

/**
 * The timeline's multi-bar selection, read from its two owners: canvas selection for
 * item and background bars (`activeItemIds`), AudioClips for audio bars. This module is
 * the single place that joins them; gestures never read selection directly — they
 * snapshot it once at gesture start via `captureTimelineGroupForGesture`.
 */

/** Whether a pointer/mouse interaction carries ANY selection modifier (shift / cmd / ctrl). */
export const hasMultiSelectModifier = (e: {shiftKey: boolean; metaKey: boolean; ctrlKey: boolean}): boolean => {
  return e.shiftKey || e.metaKey || e.ctrlKey;
};

/** Maps an interaction's modifier keys to the track selection policy's inputs: cmd/ctrl toggles the bar, shift extends the selection to it. */
export const getTimelineBarClickOptions = (e: {shiftKey: boolean; metaKey: boolean; ctrlKey: boolean}): TimelineBarClickOptions => {
  return {withToggleModifier: e.metaKey || e.ctrlKey, withExtendModifier: e.shiftKey};
};

const EMPTY_SELECTED_IDS: string[] = [];

const getActiveItemIds = (state: RootState): string[] | undefined => state.posterEditor.activeItemIds[state.posterEditor.currentPageId];

const getCurrentPageItemsHashMap = (state: RootState): Record<string, ItemObject> | undefined =>
  state.posterEditor.posterObject?.pages?.pagesHashMap?.[state.posterEditor.currentPageId]?.items?.itemsHashMap;

/**
 * The canvas-selected ids that own a timeline bar — item-track items AND backgrounds;
 * transcripts have their own track and selection model. Memoized: subscribed components
 * read it on every store dispatch (30Hz during playback), so the filter must not
 * recompute or change identity unless the selection or the item map actually changed.
 */
export const getSelectedCanvasBarIds = createSelector([getActiveItemIds, getCurrentPageItemsHashMap], (activeItemIds, itemsHashMap): string[] => {
  if (!activeItemIds?.length || !itemsHashMap) {
    return EMPTY_SELECTED_IDS;
  }
  return activeItemIds.filter((uid) => {
    const item = itemsHashMap[uid];
    return item !== undefined && item.gitype !== ITEM_TYPE.TRANSCRIPT;
  });
});

/** The selected bars that live on the ITEM track — backgrounds sit on their own track with their own row layout. */
export const getSelectedItemTrackIds = createSelector([getSelectedCanvasBarIds, getCurrentPageItemsHashMap], (canvasBarIds, itemsHashMap): string[] => {
  return canvasBarIds.filter((uid) => itemsHashMap?.[uid]?.isBackground === false);
});

/** The selected bars that live on the BACKGROUND track. */
export const getSelectedBackgroundTrackIds = createSelector([getSelectedCanvasBarIds, getCurrentPageItemsHashMap], (canvasBarIds, itemsHashMap): string[] => {
  return canvasBarIds.filter((uid) => itemsHashMap?.[uid]?.isBackground === true);
});

/** The audio side of the timeline selection — the slice mirror of AudioClips.selectedAudioItemUIDs. */
export const getSelectedAudioTrackIds = (state: RootState): string[] => {
  return state.posterEditor.selectedAudioItemUIDs;
};

export const isBarInTimelineSelection = (state: RootState, barId: string, barType: TimelineItemType): boolean => {
  if (barType === TimelineItemType.ITEM || barType === TimelineItemType.BACKGROUND) {
    return getSelectedCanvasBarIds(state).includes(barId);
  }
  if (barType === TimelineItemType.AUDIO) {
    return getSelectedAudioTrackIds(state).includes(barId);
  }
  return false;
};

/** Every selected bar as a {id, type} ref, in the timeline's own vocabulary. */
const getSelectedBarRefs = (state: RootState): TimelineBarRef[] => {
  return [
    ...getSelectedItemTrackIds(state).map((uid): TimelineBarRef => ({id: uid, type: TimelineItemType.ITEM})),
    ...getSelectedBackgroundTrackIds(state).map((uid): TimelineBarRef => ({id: uid, type: TimelineItemType.BACKGROUND})),
    ...getSelectedAudioTrackIds(state).map((uid): TimelineBarRef => ({id: uid, type: TimelineItemType.AUDIO})),
  ];
};

interface TimelineSweepableBar extends TimelineBarRef, TimeSpan {
  /** Row position in the timeline's visual stacking: item-track rows on top, then the single background row, then audio rows. */
  visualRow: number;
}

/** Every shift-selectable bar on the current page with its span, stacked in the order the tracks render: items, backgrounds, audio. */
const getSweepableBars = (state: RootState): TimelineSweepableBar[] => {
  const currentPage = state.posterEditor.posterObject?.pages?.pagesHashMap?.[state.posterEditor.currentPageId];
  if (!currentPage) {
    return [];
  }
  const bars: TimelineSweepableBar[] = [];
  const backgrounds: ItemObject[] = [];
  let maxItemRow = -1;
  for (const item of Object.values(currentPage.items.itemsHashMap)) {
    if (item.gitype === ITEM_TYPE.TRANSCRIPT) {
      continue;
    }
    if (item.isBackground) {
      backgrounds.push(item);
      continue;
    }
    bars.push({id: item.uid, type: TimelineItemType.ITEM, visualRow: item.timelineSubRow, startTime: item.onPageStartTime, endTime: getItemPageEndTime(item)});
    maxItemRow = Math.max(maxItemRow, item.timelineSubRow);
  }
  // The whole background sequence shares ONE row; the time filter decides which of them a sweep takes.
  const backgroundRow = maxItemRow + 1;
  for (const background of backgrounds) {
    bars.push({id: background.uid, type: TimelineItemType.BACKGROUND, visualRow: backgroundRow, startTime: background.onPageStartTime, endTime: getItemPageEndTime(background)});
  }
  const firstAudioRow = backgrounds.length > 0 ? backgroundRow + 1 : backgroundRow;
  for (const audio of Object.values(currentPage.audioClips.audioItemsHashMap)) {
    const startTime = audio.onPageStartTime;
    bars.push({id: audio.uid, type: TimelineItemType.AUDIO, visualRow: firstAudioRow + audio.timelineSubRow, startTime, endTime: startTime + getPlaybackDuration(audio.audioPlayer)});
  }
  return bars;
};

/** Half-open spans overlap when each starts before the other ends — bars that merely touch do not. */
const doTimeSpansOverlap = (a: TimeSpan, b: TimeSpan): boolean => {
  return a.startTime < b.endTime && b.startTime < a.endTime;
};

/** The smallest span covering every given one. */
const getBoundingTimeSpan = (spans: TimeSpan[]): TimeSpan => {
  return {startTime: Math.min(...spans.map((span) => span.startTime)), endTime: Math.max(...spans.map((span) => span.endTime))};
};

/**
 * The bars a shift-click on `target` adds to the selection: the target itself, plus —
 * when the target shares time with the current selection — every bar on every row
 * between the selection's rows and the target's row whose span overlaps the rectangle
 * they bound together. A target that shares no time with the selection is just added
 * on its own, the way a cmd/ctrl-click adds it: sweeping rows between two bars that
 * don't line up in time would take bars the user never pointed at.
 */
const computeTimelineSelectionSweep = (bars: TimelineSweepableBar[], selectedBars: TimelineSweepableBar[], target: TimelineSweepableBar): TimelineSweepableBar[] => {
  if (selectedBars.length === 0) {
    return [target];
  }
  const selectionWindow = getBoundingTimeSpan(selectedBars);
  if (!doTimeSpansOverlap(selectionWindow, target)) {
    return [target];
  }
  const sweepWindow = getBoundingTimeSpan([selectionWindow, target]);
  const sweptRows = [...selectedBars, target].map((bar) => bar.visualRow);
  const lowRow = Math.min(...sweptRows);
  const highRow = Math.max(...sweptRows);
  return bars.filter((bar) => bar.visualRow >= lowRow && bar.visualRow <= highRow && doTimeSpansOverlap(bar, sweepWindow));
};

/**
 * The popup-menu target for a right-clicked timeline bar: the bar's own fabric object,
 * widened to the whole ActiveSelection when the bar is part of the canvas
 * multi-selection — so the popup routes to the group menu, the same menu a canvas
 * right-click on the selection gets. Audio bars have no fabric object (undefined).
 */
export const getTimelineBarContextTarget = (barId: string, barType: TimelineItemType): FabricObject | undefined => {
  const page = window.posterEditor?.whiteboard?.getCurrentPage();
  const target = barType === TimelineItemType.ITEM ? page?.items.itemsHashMap[barId]?.fabricObject : undefined;
  if (!target) {
    return undefined;
  }
  const activeObjects = page?.activeSelection.getActiveObjects() ?? [];
  if (activeObjects.length > 1 && activeObjects.includes(target)) {
    return page?.activeSelection.getActiveObject() ?? undefined;
  }
  return target;
};

/** Selection ∪ swept bars, selection order first — a shift-click only ever grows the selection. */
const unionUids = (selectedUids: string[], sweptUids: string[]): string[] => {
  return [...selectedUids, ...sweptUids.filter((uid) => !selectedUids.includes(uid))];
};

/**
 * Applies a shift-click ending on the given bar: adds the swept bars (see
 * computeTimelineSelectionSweep) to whatever is selected across all three
 * multi-selectable tracks — items, backgrounds and audio. Purely accumulative: nothing
 * already selected is ever dropped, and there is no anchor — each shift-click sweeps
 * from the selection as it stands. With nothing selected it selects just the target.
 */
export const extendTimelineSelection = (targetId: string, targetType: TimelineItemType): void => {
  const page = window.posterEditor?.whiteboard?.getCurrentPage();
  if (!page) {
    return;
  }
  trackTimelineMultiSelectOnceIfNeeded(TimelineMultiSelectType.SHIFT_CLICK);
  const state = window.PMW.redux.store.getState();
  const bars = getSweepableBars(state);
  const target = bars.find((bar) => bar.id === targetId && bar.type === targetType);
  if (!target) {
    return;
  }
  const selectedBars = getSelectedBarRefs(state)
    .map((selected) => bars.find((bar) => bar.id === selected.id && bar.type === selected.type))
    .filter((bar) => bar !== undefined);
  const sweptBars = computeTimelineSelectionSweep(bars, selectedBars, target);

  const canvasBarUids = unionUids(
    getSelectedCanvasBarIds(state),
    sweptBars.filter((bar) => bar.type !== TimelineItemType.AUDIO).map((bar) => bar.id)
  );
  const items = canvasBarUids.map((uid) => page.items.itemsHashMap[uid]).filter((item) => item !== undefined);
  page.activeSelection.setTimelineMultiSelection(items);
  page.audioClips.setSelectedAudioItems(
    unionUids(
      getSelectedAudioTrackIds(state),
      sweptBars.filter((bar) => bar.type === TimelineItemType.AUDIO).map((bar) => bar.id)
    )
  );
};

/** The member matching a bar identity within a gesture snapshot. */
export const findTimelineGroupMember = (members: TimelineGroupGestureMember[], barId: string, barType: TimelineItemType): TimelineGroupGestureMember | undefined => {
  return members.find((member) => member.id === barId && member.type === barType);
};

/** One landing preview (drop ghost) on a track — the model-space rect the ghost renders from. */
export interface DropGhostBar {
  uid: string;
  startTime: number;
  endTime: number;
  subRow: number;
}

const NO_DROP_GHOST_BARS: DropGhostBar[] = [];

/**
 * The landing previews for an in-flight drag on `variant`'s track: one ghost per
 * dragged bar of that track. A single-bar drag ghosts the grabbed bar at the gesture's
 * live span; a group drag projects every same-track member by the shared time delta
 * and the grabbed bar's row delta — the same deltas the commit applies, so the ghosts
 * show exactly where the bars will land. A FOREIGN bar hovering this track on a
 * convert-on-drop drag (background → item) ghosts at the row the converted bar lands
 * on. Cross-track co-members get no ghost: their rows never change and their bars
 * already render at their landing span. Empty when no drag is targeting a row on
 * this track.
 */
export const getDropGhostBars = (modifying: TimelineModifyingState | undefined, variant: TimelineItemType): DropGhostBar[] => {
  if (modifying?.action.type !== 'dragging') {
    return NO_DROP_GHOST_BARS;
  }
  if (modifying.grabbed.type !== variant) {
    const foreignTarget = modifying.dropTarget;
    // A gap hover shows the row-insertion indicator instead of a ghost, mirroring own-track drags.
    if (foreignTarget?.trackType === variant && foreignTarget.insertionIndex === undefined) {
      return [{uid: modifying.grabbed.id, startTime: modifying.startTime, endTime: modifying.endTime, subRow: foreignTarget.row}];
    }
    return NO_DROP_GHOST_BARS;
  }
  if (modifying.targetSubRow === undefined) {
    return NO_DROP_GHOST_BARS;
  }
  const members = modifying.groupMembers;
  const grabbedMember = members ? findTimelineGroupMember(members, modifying.grabbed.id, modifying.grabbed.type) : undefined;
  if (!members || !grabbedMember) {
    return [{uid: modifying.grabbed.id, startTime: modifying.startTime, endTime: modifying.endTime, subRow: modifying.targetSubRow}];
  }
  const timeDelta = modifying.startTime - grabbedMember.originalStartTime;
  const rowDelta = modifying.targetSubRow - grabbedMember.originalSubRow;
  return members
    .filter((member) => member.type === variant)
    .map((member): DropGhostBar => {
      return {
        uid: member.id,
        startTime: member.originalStartTime + timeDelta,
        endTime: member.originalEndTime + timeDelta,
        subRow: member.originalSubRow + rowDelta,
      };
    });
};

/** Splits a gesture snapshot into the per-owner uid lists the page orchestrators take. */
export const splitTimelineGroupMemberUids = (members: TimelineGroupGestureMember[]): {itemUids: string[]; backgroundUids: string[]; audioUids: string[]} => {
  return {
    itemUids: members.filter((member) => member.type === TimelineItemType.ITEM).map((member) => member.id),
    backgroundUids: members.filter((member) => member.type === TimelineItemType.BACKGROUND).map((member) => member.id),
    audioUids: members.filter((member) => member.type === TimelineItemType.AUDIO).map((member) => member.id),
  };
};

/**
 * Snapshots the multi-bar selection for a DRAG that just started on `grabbedId`.
 *
 * Co-selected BACKGROUNDS are left out: their start times are derived from the
 * sequence tiling, so they have no free position to shift by a group delta. A
 * background co-selected with items keeps its place while the items move.
 */
export const captureTimelineGroupForDrag = (state: RootState, grabbedId: string, grabbedType: TimelineItemType): TimelineGroupGestureMember[] | undefined => {
  return captureTimelineGroup(state, grabbedId, grabbedType, {withBackgrounds: false});
};

/**
 * Snapshots the multi-bar selection for a RESIZE that just started on `grabbedId`.
 * Backgrounds join in: a sequence bar has no free start time, but it does have a
 * duration, so the grabbed edge's delta resizes it like any other member and the
 * sequence re-tiles around the result.
 */
export const captureTimelineGroupForResize = (state: RootState, grabbedId: string, grabbedType: TimelineItemType): TimelineGroupGestureMember[] | undefined => {
  return captureTimelineGroup(state, grabbedId, grabbedType, {withBackgrounds: true});
};

/**
 * The shared snapshot builder: every member's id, kind, span, row, and resize bounds.
 * Returns undefined when the grabbed bar is not part of a selection of two or more
 * eligible bars — the gesture stays a single-bar one and none of the group machinery
 * engages.
 */
const captureTimelineGroup = (
  state: RootState,
  grabbedId: string,
  grabbedType: TimelineItemType,
  {withBackgrounds}: {withBackgrounds: boolean}
): TimelineGroupGestureMember[] | undefined => {
  const currentPage = state.posterEditor.posterObject?.pages?.pagesHashMap?.[state.posterEditor.currentPageId];
  if (!currentPage) {
    return undefined;
  }

  const members: TimelineGroupGestureMember[] = [];
  for (const uid of getSelectedItemTrackIds(state)) {
    const itemObject = currentPage.items.itemsHashMap[uid];
    members.push({
      id: uid,
      type: TimelineItemType.ITEM,
      originalStartTime: itemObject.onPageStartTime,
      originalEndTime: getItemPageEndTime(itemObject),
      originalSubRow: itemObject.timelineSubRow,
      resizeBounds: getItemBarResizeBounds(itemObject),
    });
  }
  if (withBackgrounds) {
    for (const uid of getSelectedBackgroundTrackIds(state)) {
      const backgroundObject = currentPage.items.itemsHashMap[uid];
      members.push({
        id: uid,
        type: TimelineItemType.BACKGROUND,
        originalStartTime: backgroundObject.onPageStartTime,
        originalEndTime: getItemPageEndTime(backgroundObject),
        // The whole background sequence renders on one row; nothing ever row-shifts it.
        originalSubRow: 0,
        resizeBounds: getItemBarResizeBounds(backgroundObject),
      });
    }
  }
  for (const uid of getSelectedAudioTrackIds(state)) {
    const audioObject = currentPage.audioClips.audioItemsHashMap[uid];
    if (!audioObject) {
      continue;
    }
    const span = {startTime: audioObject.onPageStartTime, endTime: audioObject.onPageStartTime + getPlaybackDuration(audioObject.audioPlayer)};
    members.push({
      id: uid,
      type: TimelineItemType.AUDIO,
      originalStartTime: span.startTime,
      originalEndTime: span.endTime,
      originalSubRow: audioObject.timelineSubRow,
      resizeBounds: getAudioTimelineResizeBounds(span, audioObject.audioPlayer),
    });
  }

  const isGrabbedBarInSelection = members.some((member) => member.id === grabbedId && member.type === grabbedType);
  return members.length >= 2 && isGrabbedBarInSelection ? members : undefined;
};
